import { PermissionEnum } from '@/constants/enums/PermissionEnum';
import { TrainingTypeEnum } from '@/constants/trainingTypeOptions';
import { ActualUser } from '@/state/ActualUser.atom';
import { UserTypes, UserTypesPermissions } from '../constants/SGFTUserTypes';
import { msalInstance } from '../main';

export const hasPermissions = (...permissions: PermissionEnum[]) => {
	const roles = getActualRole();
	return permissions.every((permission) =>
		roles?.some((role) =>
			UserTypesPermissions[role as keyof typeof UserTypesPermissions]?.includes(
				permission,
			),
		),
	);
};

export const getActualRole = () => {
	const account =
		msalInstance?.getActiveAccount() ??
		msalInstance?.getAllAccounts()[0];
	return account?.idTokenClaims?.roles ?? [];
};

export const isInRole = (role: string) => {
	return getActualRole()?.includes(role);
};

export const isReadOnlyUser = () => {
	const roles = getActualRole();
	return roles?.includes(UserTypes.ReadOnly);
};

export const userCanSave = (
	targetManagementId: number | null,
	userManagementsInfo: Pick<
		ActualUser,
		'management_id' | 'whitelisted_managements_ids'
	>,
) => {
	const roles = getActualRole();

	if (roles?.includes(UserTypes.Admin)) return true;
	if (roles?.includes(UserTypes.Rh)) return true;
	if (roles?.includes(UserTypes.Manager)) return true;

	if (!targetManagementId) return false;

	const managementsIds = new Set([
		userManagementsInfo.management_id,
		...userManagementsInfo.whitelisted_managements_ids,
	]);
	return managementsIds.has(targetManagementId);
};

export const userIsRhOrAdmin = () => {
	const roles = getActualRole();

	if (roles?.includes(UserTypes.Admin)) {
		return true;
	}

	return roles?.includes(UserTypes.Rh);
};

export const userCanSaveTraining = (trainingTtpe?: TrainingTypeEnum) => {
	const roles = getActualRole();

	if (roles?.includes(UserTypes.Admin)) {
		return true;
	}
	if (trainingTtpe === TrainingTypeEnum.REGULATORY) {
		return roles?.includes(UserTypes.QsmsTreinamento);
	} else if (trainingTtpe === TrainingTypeEnum.PROCEDURE) {
		return (
			roles?.includes(UserTypes.RhTreinamento) || roles?.includes(UserTypes.Rh)
		);
	}
	return false;
};

export const userCanExtractRegulatoryTraining = () => {
	const roles = getActualRole();

	if (roles?.includes(UserTypes.Admin)) {
		return true;
	}

	return (
		roles?.includes(UserTypes.QsmsTreinamento) ||
		roles?.includes(UserTypes.Rh) ||
		roles?.includes(UserTypes.RhTreinamento)
	);
};

export const userCanSeeTraining = () => {
	const roles = getActualRole();

	if (roles?.includes(UserTypes.Admin)) {
		return true;
	}
	if (
		roles?.includes(UserTypes.QsmsTreinamento) ||
		roles?.includes(UserTypes.RhTreinamento)
	) {
		return true;
	}

	if (roles?.includes(UserTypes.ReadOnly)) {
		return false;
	}
	return true;
};

export const userCanSeeTrainingControl = () => {
	const roles = getActualRole();

	if (hasPermissions(PermissionEnum.TrainingControlCanSee)) {
		return true;
	}
	if (roles?.includes(UserTypes.ReadOnly)) {
		return false;
	}

	return true;
};

export const userCanSeeTrainingControlUploadButtons = () => {
	const roles = getActualRole();

	if (hasPermissions(PermissionEnum.TrainingControlCanUpload)) {
		return true;
	}
	if (roles?.includes(UserTypes.ReadOnly)) {
		return false;
	}
	return false;
};

export const userCanSeeTeamStructureForms = () => {
	const roles = getActualRole();
	if (roles?.includes(UserTypes.ReadOnly)) {
		return false;
	}
	if (roles?.includes(UserTypes.Admin)) {
		return true;
	}
	return roles?.includes(UserTypes.Rh);
};

export const shouldDisableScheduleMenu = () => {
	const roles = getActualRole();

	if (roles?.includes(UserTypes.OnDutyWorker)) {
		return false;
	}

	return roles?.includes(UserTypes.ReadOnly);
};

export const isOnDutyWorker = () => {
	const roles = getActualRole();
	return roles?.includes(UserTypes.OnDutyWorker);
};

export const userCanSaveAllocation = () => {
	const roles = getActualRole();

	if (roles?.includes(UserTypes.Admin)) {
		return true;
	}

	return roles?.includes(UserTypes.Rh);
};

export const isAdmin = () => {
	return getActualRole()?.includes(UserTypes.Admin);
};

export const isRegulatorio = () => {
	return getActualRole()?.includes(UserTypes.Regulatorio);
};

export const userCanEditRequirementControl = () => {
	return hasPermissions(PermissionEnum.RequirementControlCanEdit);
};

export const userCanSeeRequirementControl = () => {
	return hasPermissions(PermissionEnum.RequirementControlCanSee);
};

export const userCanSeeTurnstileAccessAndParameters = () => {
	const roles = getActualRole();

	if (roles?.includes(UserTypes.Admin)) {
		return true;
	}

	if (
		roles?.includes(UserTypes.Rh) ||
		roles?.includes(UserTypes.RhTreinamento) ||
		roles?.includes(UserTypes.TurnstileAccess) ||
		roles?.includes(UserTypes.ViewTurnstile)
	) {
		return true;
	}

	if (roles?.includes(UserTypes.ReadOnly)) {
		return false;
	}
	return false;
};

export const userCanToggleShowAllTurnstileAccess = () => {
	const roles = getActualRole();

	const allowedRoles = [UserTypes.ViewTurnstile];

	return allowedRoles.some((role) => roles?.includes(role));
};

export const userCanBlockTraining = () => {
	const roles = getActualRole();

	if (roles?.includes(UserTypes.Admin)) {
		return true;
	}

	return roles?.includes(UserTypes.RhTreinamento);
};

export const userCanMapRegulatoryTraining = () => {
	const roles = getActualRole();

	if (roles?.includes(UserTypes.Admin)) {
		return true;
	}

	return roles?.includes(UserTypes.QsmsTreinamento);
};

export const userCanClearAllEmployees = () => {
	const roles = getActualRole();

	if (roles?.includes(UserTypes.Admin)) {
		return true;
	}

	return roles?.includes(UserTypes.TurnstileAccess);
};

export const userCanRunTurnstileRoutine = () => {
	const roles = getActualRole();

	const allowedRoles = [
		UserTypes.Admin,
		UserTypes.RhTreinamento,
		UserTypes.TurnstileAccess,
	];

	return allowedRoles.some((role) => roles?.includes(role));
};

export const userCanUpdateActivity = () => {
	const roles = getActualRole();

	return (
		roles?.includes(UserTypes.Admin) ||
		roles?.includes(UserTypes.QsmsTreinamento)
	);
};

export const userOnlyCanSeeAllEmployeeByActivityPole = () => {
	const roles = getActualRole();
	const rolesThatOnlyCanSeeAllEmployeeByActivityPole = [
		UserTypes.Admin,
		UserTypes.Rh,
		UserTypes.RhTreinamento,
	];
	return rolesThatOnlyCanSeeAllEmployeeByActivityPole.some((role) =>
		roles?.includes(role),
	);
};
